import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "../utils/axiosInstance";
import {
  Modal,
  Button,
  Form,
  Table,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { getApiBase } from "../utils/useApiBase";
const API_URL = `${getApiBase()}/ubukungu`;

const Ubukungu = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  const [windowHeight, setWindowHeight] = useState(typeof window !== "undefined" ? window.innerHeight : 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(50);
  const [showAllData, setShowAllData] = useState(true);
  const [orientation, setOrientation] = useState(
    typeof window !== "undefined" ? (window.innerWidth > window.innerHeight ? "landscape" : "portrait") : "portrait"
  );
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const observerRef = useRef(null);

  const [stats, setStats] = useState({ total: 0, withLand: 0, withAnimals: 0, withAccount: 0 });

  const [form, setForm] = useState({
    ubukungu_id: null, muturage_id: "", amazina_y_umuturage: "",
    umurimo: "", aho_akorera: "", inkomoko_y_amafaranga: "",
    afite_ubutaka: 0, afite_amatungo: 0, afite_konti_yo_kuzigama: 0,
  });

  const [formErrors, setFormErrors] = useState({});

  // ═══════════════════════════════════════════
  // DEVICE DETECTION
  // ═══════════════════════════════════════════
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
      setOrientation(window.innerWidth > window.innerHeight ? "landscape" : "portrait");
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const device = useMemo(() => {
    const w = windowWidth;
    const h = windowHeight;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
    const isAndroid = /Android/.test(ua);
    const isSamsung = /Samsung|SM-/.test(ua);
    const isPixel = /Pixel/.test(ua);
    const hasNotch = isIOS && w >= 375 && h >= 812;
    const hasDynamicIsland = isIOS && w >= 393 && h >= 852;
    const isGalaxyFold = w <= 300 || (isAndroid && w <= 320);
    const isFoldUnfolded = isAndroid && w >= 700 && w <= 900;

    return {
      isGalaxyFold: isGalaxyFold,
      isMiniPhone: w <= 320,
      isSmallPhone: w > 320 && w <= 375,
      isMediumPhone: w > 375 && w <= 414,
      isLargePhone: w > 414 && w < 576,
      isSmallTablet: w >= 576 && w < 768,
      isTablet: w >= 768 && w < 992,
      isLargeTablet: w >= 992 && w < 1200,
      isDesktop: w >= 1200,
      isLargeDesktop: w >= 1440,
      isUltraWide: w >= 1920,

      isMobile: w < 576,
      isTabletOrAbove: w >= 576,
      isDesktopOrAbove: w >= 992,

      isIOS, isAndroid, isSamsung, isPixel,
      hasNotch, hasDynamicIsland, isGalaxyFoldDevice: isGalaxyFold,
      isFoldUnfolded,

      isLandscape: orientation === "landscape",
      isPortrait: orientation === "portrait",
      isLandscapePhone: orientation === "landscape" && w < 992 && h < 500,

      isTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      isRetina: window.devicePixelRatio >= 2,

      width: w, height: h,
    };
  }, [windowWidth, windowHeight, orientation]);

  // ═══════════════════════════════════════════
  // THEME
  // ═══════════════════════════════════════════
  const t = {
    bg: "#ffffff", card: "#ffffff", card2: "#f0f4fb", text: "#0d1b2a",
    muted: "#64748b", dim: "#64748b", faint: "#64748b",
    primary: "#0B3D91", primaryDk: "#093278", primaryLt: "#0B3D91",
    success: "#16a34a", successLt: "#16a34a",
    warning: "#d97706", warningLt: "#d97706",
    danger: "#dc2626", dangerLt: "#dc2626",
    info: "#0369a1", infoLt: "#0369a1",
    border: "#e2e8f0", border2: "#e2e8f0", border3: "#e2e8f0",
  };

  // ═══════════════════════════════════════════
  // RESPONSIVE SPACING
  // ═══════════════════════════════════════════
  const sp = useMemo(() => {
    if (device.isGalaxyFold) return { p: "0.35rem", gap: "0.35rem", card: "0.4rem", radius: "10px" };
    if (device.isMiniPhone) return { p: "0.4rem", gap: "0.4rem", card: "0.5rem", radius: "12px" };
    if (device.isSmallPhone) return { p: "0.5rem", gap: "0.5rem", card: "0.6rem", radius: "14px" };
    if (device.isMediumPhone) return { p: "0.6rem", gap: "0.5rem", card: "0.75rem", radius: "14px" };
    if (device.isLargePhone) return { p: "0.65rem", gap: "0.6rem", card: "0.85rem", radius: "16px" };
    if (device.isSmallTablet) return { p: "0.75rem", gap: "0.65rem", card: "1rem", radius: "16px" };
    if (device.isTablet) return { p: "1rem", gap: "0.75rem", card: "1.15rem", radius: "18px" };
    if (device.isLargeTablet) return { p: "1.25rem", gap: "0.85rem", card: "1.25rem", radius: "20px" };
    return { p: "1.5rem", gap: "1rem", card: "1.5rem", radius: "22px" };
  }, [device]);

  const fs = useMemo(() => {
    if (device.isGalaxyFold) return { xs: "0.5rem", sm: "0.6rem", md: "0.7rem", lg: "0.85rem", xl: "1rem", xxl: "1.1rem", stat: "1rem", icon: "0.8rem" };
    if (device.isMiniPhone) return { xs: "0.55rem", sm: "0.62rem", md: "0.72rem", lg: "0.88rem", xl: "1.05rem", xxl: "1.15rem", stat: "1.15rem", icon: "0.85rem" };
    if (device.isSmallPhone) return { xs: "0.6rem", sm: "0.65rem", md: "0.78rem", lg: "0.92rem", xl: "1.08rem", xxl: "1.2rem", stat: "1.35rem", icon: "0.95rem" };
    if (device.isMediumPhone) return { xs: "0.62rem", sm: "0.68rem", md: "0.82rem", lg: "0.95rem", xl: "1.1rem", xxl: "1.25rem", stat: "1.5rem", icon: "1.05rem" };
    if (device.isLargePhone) return { xs: "0.65rem", sm: "0.7rem", md: "0.85rem", lg: "0.98rem", xl: "1.12rem", xxl: "1.3rem", stat: "1.65rem", icon: "1.1rem" };
    if (device.isSmallTablet) return { xs: "0.68rem", sm: "0.72rem", md: "0.85rem", lg: "1rem", xl: "1.2rem", xxl: "1.4rem", stat: "1.75rem", icon: "1.15rem" };
    if (device.isTablet) return { xs: "0.7rem", sm: "0.75rem", md: "0.88rem", lg: "1.05rem", xl: "1.3rem", xxl: "1.5rem", stat: "1.9rem", icon: "1.25rem" };
    if (device.isLargeTablet) return { xs: "0.72rem", sm: "0.78rem", md: "0.88rem", lg: "1.05rem", xl: "1.35rem", xxl: "1.55rem", stat: "2rem", icon: "1.35rem" };
    return { xs: "0.75rem", sm: "0.8rem", md: "0.9rem", lg: "1.1rem", xl: "1.5rem", xxl: "1.75rem", stat: "2.25rem", icon: "1.5rem" };
  }, [device]);

  // ═══════════════════════════════════════════
  // INFINITE SCROLL FOR SHOWING ALL DATA
  // ═══════════════════════════════════════════
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && visibleCount < filteredData.length) {
          setVisibleCount((prev) => Math.min(prev + 30, filteredData.length));
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, filteredData.length, visibleCount]
  );

  const visibleData = useMemo(() => {
    return showAllData ? filteredData.slice(0, visibleCount) : filteredData;
  }, [filteredData, visibleCount, showAllData]);

  // ═══════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
    if (formErrors[name]) setFormErrors((fe) => ({ ...fe, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!(form.amazina_y_umuturage || "").trim()) errors.amazina_y_umuturage = "Amazina ni ngombwa";
    if (!(form.muturage_id || "").trim()) errors.muturage_id = "Indangamuntu ni ngombwa";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const showAlertMsg = (msg, variant = "success") => {
    setAlert({ show: true, message: msg, variant });
    setTimeout(() => setAlert({ show: false, message: "", variant: "" }), 4000);
  };

  const calculateStats = (arr) => {
    setStats({
      total: arr.length,
      withLand: arr.filter((i) => i.afite_ubutaka === 1).length,
      withAnimals: arr.filter((i) => i.afite_amatungo === 1).length,
      withAccount: arr.filter((i) => i.afite_konti_yo_kuzigama === 1).length,
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.ubukungu ?? []);
      setData(data);
      setFilteredData(data);
      calculateStats(data);
      setVisibleCount(50);
    } catch {
      showAlertMsg("Habaye ikosa mu gutegura amakuru", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Household heads already registered under Abaturage, for the auto-populate
  // dropdown below instead of hand-typing a name/ID.
  const [citizens, setCitizens] = useState([]);
  useEffect(() => {
    axios.get(`${getApiBase()}/isibo/household-heads`)
      .then((res) => setCitizens(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCitizens([]));
  }, []);

  const handleCitizenSelect = (e) => {
    const citizenId = e.target.value;
    const citizen = citizens.find((c) => String(c.umuturage_id) === String(citizenId));
    setForm((prev) => ({
      ...prev,
      muturage_id: citizenId,
      amazina_y_umuturage: citizen ? citizen.full_name : "",
    }));
    if (formErrors.muturage_id) setFormErrors((prev) => ({ ...prev, muturage_id: "", amazina_y_umuturage: "" }));
  };

  useEffect(() => {
    let result = [...data];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (i) =>
          i.amazina_y_umuturage?.toLowerCase().includes(term) ||
          i.muturage_id?.toLowerCase().includes(term) ||
          i.umurimo?.toLowerCase().includes(term) ||
          i.aho_akorera?.toLowerCase().includes(term) ||
          i.inkomoko_y_amafaranga?.toLowerCase().includes(term)
      );
    }
    if (filterType !== "all") {
      const map = { ubutaka: "afite_ubutaka", amatungo: "afite_amatungo", konti: "afite_konti_yo_kuzigama" };
      if (map[filterType]) result = result.filter((i) => i[map[filterType]] === 1);
    }
    setFilteredData(result);
    setVisibleCount(50);
  }, [searchTerm, filterType, data]);

  const handleAdd = () => {
    setEditingId(null);
    setForm({ ubukungu_id: null, muturage_id: "", amazina_y_umuturage: "", umurimo: "", aho_akorera: "", inkomoko_y_amafaranga: "", afite_ubutaka: 0, afite_amatungo: 0, afite_konti_yo_kuzigama: 0 });
    setFormErrors({});
    setShow(true);
    setShowMobileMenu(false);
  };

  const handleEdit = (item) => { setEditingId(item.ubukungu_id); setForm({ ...item, muturage_id: item.umuturage_id ?? item.muturage_id ?? "" }); setFormErrors({}); setShow(true); };
  const handleViewDetails = (item) => { setSelectedItem(item); setShowDetails(true); };
  const handleDelete = (id) => { const item = data.find((d) => d.ubukungu_id === id); if (item) { setItemToDelete(item); setShowDeleteConfirm(true); } };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try { await axios.delete(`${API_URL}/${itemToDelete.ubukungu_id}`); showAlertMsg("Yasibwe neza!"); loadData(); }
    catch { showAlertMsg("Ntibikunze gusiba!", "danger"); }
    finally { setShowDeleteConfirm(false); setItemToDelete(null); }
  };

  const handleSubmit = async () => {
    if (!validateForm()) { showAlertMsg("Uzuza amakuru akenewe!", "warning"); return; }
    try {
      if (editingId === null) { await axios.post(API_URL, form); showAlertMsg("Byashyizwemo neza!"); }
      else { await axios.put(`${API_URL}/${editingId}`, form); showAlertMsg("Byavuguruwe neza!"); }
      setShow(false); loadData();
    } catch { showAlertMsg("Ntibikunze kubika!", "danger"); }
  };

  const exportToCSV = () => {
    const h = ["Amazina", "ID", "Umurimo", "Aho Akorera", "Inkomoko", "Ubutaka", "Amatungo", "Konti"];
    const rows = filteredData.map((i) => [i.amazina_y_umuturage, i.muturage_id, i.umurimo, i.aho_akorera, i.inkomoko_y_amafaranga, i.afite_ubutaka ? "Yego" : "Oya", i.afite_amatungo ? "Yego" : "Oya", i.afite_konti_yo_kuzigama ? "Yego" : "Oya"]);
    const csv = [h, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ubukungu_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    showAlertMsg("Excel yakoboye neza!");
    setShowMobileMenu(false);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // ═══════════════════════════════════════════
  // SUB-COMPONENTS
  // ═══════════════════════════════════════════
  const BoolBadge = ({ val, yesColor = "success" }) => (
    <Badge
      bg={val ? yesColor : "danger"}
      pill
      style={{ fontSize: fs.xs, padding: `0.2rem ${device.isMobile ? "0.35rem" : "0.55rem"}` }}
    >
      <i className={`bi bi-${val ? "check-circle" : "x-circle"} me-1`} style={{ fontSize: "0.6em" }} />
      {val ? "Yego" : "Oya"}
    </Badge>
  );

  const StatusDot = ({ active, color }) => (
    <div style={{
      width: device.isGalaxyFold ? 6 : device.isMobile ? 8 : 10,
      height: device.isGalaxyFold ? 6 : device.isMobile ? 8 : 10,
      borderRadius: "50%",
      background: active ? color : "#cbd5e1",
      transition: "all 0.3s",
      boxShadow: active ? `0 0 6px ${color}50` : "none",
    }} />
  );

  const DetailField = ({ label, value, color }) => (
    <div style={{
      borderLeft: `3px solid ${color || t.border2}`,
      paddingLeft: device.isMobile ? "0.5rem" : "0.85rem",
      marginBottom: device.isMobile ? "0.6rem" : "0.85rem",
    }}>
      <div style={{ color: t.dim, fontSize: fs.xs, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "0.1rem" }}>{label}</div>
      <div style={{ color: value ? t.text : t.faint, fontSize: fs.md, fontWeight: 500 }}>{value || "—"}</div>
    </div>
  );

  // ═══════════════════════════════════════════
  // MOBILE CARD
  // ═══════════════════════════════════════════
  const MobileCard = React.memo(({ item, index, isLast }) => {
    const isOpen = expandedRows.has(item.ubukungu_id);

    return (
      <div
        ref={isLast ? lastItemRef : null}
        style={{
          background: `linear-gradient(145deg, ${t.card} 0%, ${t.card2} 100%)`,
          border: `1px solid ${isOpen ? t.primary : t.border2}`,
          borderRadius: sp.radius,
          marginBottom: sp.gap,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: isOpen ? `0 6px 24px ${t.primary}18` : "0 1px 3px #ffffff3",
          animation: "fadeIn 0.3s ease",
        }}
      >
        {/* HEADER */}
        <div
          onClick={() => toggleRow(item.ubukungu_id)}
          style={{
            padding: sp.card,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: device.isGalaxyFold ? "0.3rem" : "0.5rem",
            WebkitTapHighlightColor: "transparent",
            minHeight: device.isTouch ? 48 : "auto",
          }}
        >
          {/* Number badge */}
          <span style={{
            background: `linear-gradient(135deg, ${t.primary}, ${t.primaryDk})`,
            color: "#0d1b2a",
            borderRadius: "50%",
            width: device.isGalaxyFold ? 20 : device.isMiniPhone ? 22 : 26,
            height: device.isGalaxyFold ? 20 : device.isMiniPhone ? 22 : 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: device.isGalaxyFold ? "0.5rem" : fs.xs,
            fontWeight: 700,
            flexShrink: 0,
          }}>
            {index + 1}
          </span>

          {/* Main info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: t.text,
              fontSize: device.isGalaxyFold ? "0.72rem" : fs.md,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.3,
            }}>
              {item.amazina_y_umuturage}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap", marginTop: "0.1rem" }}>
              <span style={{
                color: t.infoLt,
                background: `${t.info}15`,
                padding: "0.08rem 0.3rem",
                borderRadius: "3px",
                fontSize: device.isGalaxyFold ? "0.5rem" : fs.xs,
                fontFamily: "monospace",
              }}>
                {item.muturage_id}
              </span>
              {item.umurimo && (
                <span style={{ color: t.dim, fontSize: device.isGalaxyFold ? "0.5rem" : fs.xs }}>
                  {item.umurimo}
                </span>
              )}
            </div>
          </div>

          {/* Status dots + chevron */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexShrink: 0 }}>
            <div style={{ display: "flex", gap: "3px" }}>
              <StatusDot active={item.afite_ubutaka} color={t.success} />
              <StatusDot active={item.afite_amatungo} color={t.warning} />
              <StatusDot active={item.afite_konti_yo_kuzigama} color={t.info} />
            </div>
            <i
              className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
              style={{ color: t.dim, fontSize: fs.sm, transition: "transform 0.3s" }}
            />
          </div>
        </div>

        {/* EXPANDED */}
        {isOpen && (
          <div style={{ borderTop: `1px solid ${t.border3}`, animation: "fadeIn 0.25s ease" }}>
            {/* Info grid */}
            <div style={{
              padding: sp.card,
              display: "grid",
              gridTemplateColumns: device.isGalaxyFold ? "1fr" : "1fr 1fr",
              gap: device.isGalaxyFold ? "0.3rem" : "0.5rem",
            }}>
              <DetailField label="Umurimo" value={item.umurimo} color={t.success} />
              <DetailField label="Aho Akorera" value={item.aho_akorera} color={t.warning} />
              <div style={{ gridColumn: device.isGalaxyFold ? "1" : "1 / -1" }}>
                <DetailField label="Inkomoko y'Amafaranga" value={item.inkomoko_y_amafaranga} color={t.info} />
              </div>
            </div>

            {/* Status badges */}
            <div style={{
              padding: `0 ${sp.card} ${sp.card}`,
              display: "flex",
              gap: "0.3rem",
              flexWrap: "wrap",
            }}>
              {[
                { label: "Ubutaka", val: item.afite_ubutaka, icon: "geo-alt", clr: t.success },
                { label: "Amatungo", val: item.afite_amatungo, icon: "piggy-bank", clr: t.warning },
                { label: "Konti", val: item.afite_konti_yo_kuzigama, icon: "bank", clr: t.info },
              ].map((s) => (
                <span key={s.label} style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.2rem",
                  padding: `0.2rem ${device.isGalaxyFold ? "0.3rem" : "0.45rem"}`,
                  borderRadius: "14px",
                  fontSize: device.isGalaxyFold ? "0.5rem" : fs.xs,
                  fontWeight: 600,
                  background: s.val ? `${s.clr}18` : `${t.danger}10`,
                  color: s.val ? s.clr : t.dangerLt,
                  border: `1px solid ${s.val ? `${s.clr}30` : `${t.danger}20`}`,
                }}>
                  <i className={`bi bi-${s.icon}`} style={{ fontSize: "0.6em" }} />
                  {!device.isGalaxyFold && `${s.label}: `}{s.val ? "✓" : "✗"}
                </span>
              ))}
            </div>

            {/* Actions */}
            <div style={{
              borderTop: `1px solid ${t.border3}`,
              padding: sp.card,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: device.isGalaxyFold ? "0.25rem" : "0.4rem",
            }}>
              {[
                { label: "Reba", icon: "eye", clr: t.info, fn: () => handleViewDetails(item) },
                { label: "Hindura", icon: "pencil", clr: t.primaryLt, fn: () => handleEdit(item) },
                { label: "Siba", icon: "trash", clr: t.dangerLt, fn: () => handleDelete(item.ubukungu_id) },
              ].map((a) => (
                <button
                  key={a.label}
                  onClick={(e) => { e.stopPropagation(); a.fn(); }}
                  style={{
                    background: `${a.clr}12`,
                    border: `1px solid ${a.clr}30`,
                    color: a.clr,
                    borderRadius: "10px",
                    padding: device.isGalaxyFold ? "0.4rem" : device.isMiniPhone ? "0.5rem" : "0.6rem",
                    cursor: "pointer",
                    fontSize: device.isGalaxyFold ? "0.55rem" : fs.xs,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.25rem",
                    transition: "all 0.2s",
                    minHeight: device.isTouch ? 44 : "auto",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  <i className={`bi bi-${a.icon}`} />
                  {!device.isGalaxyFold && <span>{a.label}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  });

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════
  return (
    <div
      ref={containerRef}
      style={{
        background: t.bg,
        color: t.text,
        minHeight: "100vh",
        minHeight: "100dvh",
        padding: sp.p,
        paddingBottom: device.isMobile ? "5rem" : sp.p,
        WebkitOverflowScrolling: "touch",
        
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');

        *, *::before, *::after {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }
        html {
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
          scroll-behavior: smooth;
        }
        body {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          
          margin: 0;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        /* ═══ SAFE AREA ═══ */
        @supports (padding: env(safe-area-inset-top)) {
          .safe-wrap {
            padding-top: env(safe-area-inset-top);
            padding-bottom: env(safe-area-inset-bottom);
            padding-left: env(safe-area-inset-left);
            padding-right: env(safe-area-inset-right);
          }
          .fab-wrap {
            bottom: calc(96px + env(safe-area-inset-bottom));
            right: calc(12px + env(safe-area-inset-right));
          }
        }

        /* ═══ STAT CARDS ═══ */
        .sc {
          background: linear-gradient(145deg, #ffffff, #f0f4fb) !important;
          border: 1px solid #e2e8f0 !important;
          transition: all 0.35s cubic-bezier(0.23,1,0.32,1) !important;
          overflow: hidden;
          -webkit-transform: translateZ(0);
        }
        .sc:active { transform: scale(0.97) !important; }
        @media (hover:hover) {
          .sc:hover {
            transform: translateY(-5px) scale(1.02) !important;
            box-shadow: 0 16px 32px -8px rgba(99,102,241,0.18) !important;
            border-color: #0B3D91 !important;
          }
        }

        .ir { display:flex; align-items:center; justify-content:center; position:relative; margin:0 auto; border-radius:50%; }
        .ir::before {
          content:''; position:absolute; inset:0; border-radius:50%; padding:2px;
          background:linear-gradient(135deg, var(--rc) 0%, transparent 60%);
          -webkit-mask: linear-gradient(#0d1b2a 0 0) content-box, linear-gradient(#0d1b2a 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
        }
        .iri {
          border-radius:50%;
          background:linear-gradient(145deg,#f0f4fb,#ffffff);
          display:flex; align-items:center; justify-content:center;
        }

        /* ═══ TABLE ═══ */
        .tw {
          background: #ffffff; border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 16px 32px -8px rgba(0,0,0,0.35);
        }
        .mt { background-color: #ffffff !important; color: #0d1b2a !important; margin-bottom: 0 !important; }
        .mt thead { background: linear-gradient(180deg,#0B3D91,#ffffff) !important; }
        .mt thead th {
          background: transparent !important; color: #64748b !important;
          font-weight: 600 !important; text-transform: uppercase !important;
          border-bottom: 1px solid #e2e8f0 !important; white-space: nowrap;
          position: sticky; top: 0; z-index: 10;
        }
        .mt tbody tr {
          transition: all 0.2s !important;
          border-bottom: 1px solid #f0f4fb !important;
        }
        @media (hover:hover) {
          .mt tbody tr:hover {
            background: linear-gradient(90deg, rgba(99,102,241,0.07), rgba(99,102,241,0.02)) !important;
          }
        }
        .mt tbody td {
          background: transparent !important; color: #0d1b2a !important;
          vertical-align: middle !important; border: none !important;
        }
        .mt tbody tr:last-child { border-bottom: none !important; }

        /* ═══ RESPONSIVE TABLE SIZES ═══ */
        /* Galaxy Fold */
        @media (max-width:300px) {
          .tw { border-radius:10px; }
          .mt thead th { font-size:0.5rem !important; padding:0.5rem 0.25rem !important; letter-spacing:0.3px !important; }
          .mt tbody td { font-size:0.55rem !important; padding:0.5rem 0.25rem !important; }
        }
        /* iPhone SE / Mini phones */
        @media (min-width:301px) and (max-width:320px) {
          .tw { border-radius:12px; }
          .mt thead th { font-size:0.52rem !important; padding:0.55rem 0.3rem !important; letter-spacing:0.3px !important; }
          .mt tbody td { font-size:0.6rem !important; padding:0.55rem 0.3rem !important; }
        }
        /* iPhone 6/7/8, small Android */
        @media (min-width:321px) and (max-width:375px) {
          .tw { border-radius:14px; }
          .mt thead th { font-size:0.55rem !important; padding:0.6rem 0.3rem !important; }
          .mt tbody td { font-size:0.65rem !important; padding:0.6rem 0.3rem !important; }
        }
        /* iPhone X/11/12/13/14/15, Pixel */
        @media (min-width:376px) and (max-width:413px) {
          .tw { border-radius:15px; }
          .mt thead th { font-size:0.58rem !important; padding:0.65rem 0.35rem !important; }
          .mt tbody td { font-size:0.7rem !important; padding:0.65rem 0.35rem !important; }
        }
        /* iPhone Plus/Max, Galaxy S */
        @media (min-width:414px) and (max-width:575px) {
          .tw { border-radius:16px; }
          .mt thead th { font-size:0.6rem !important; padding:0.7rem 0.4rem !important; }
          .mt tbody td { font-size:0.72rem !important; padding:0.7rem 0.4rem !important; }
        }
        /* Small tablet / iPad Mini */
        @media (min-width:576px) and (max-width:767px) {
          .tw { border-radius:17px; }
          .mt thead th { font-size:0.62rem !important; padding:0.75rem 0.45rem !important; letter-spacing:0.5px !important; }
          .mt tbody td { font-size:0.75rem !important; padding:0.75rem 0.45rem !important; }
        }
        /* iPad / Tablet */
        @media (min-width:768px) and (max-width:991px) {
          .tw { border-radius:18px; }
          .mt thead th { font-size:0.65rem !important; padding:0.85rem 0.5rem !important; letter-spacing:0.6px !important; }
          .mt tbody td { font-size:0.8rem !important; padding:0.85rem 0.5rem !important; }
        }
        /* Large tablet / Small desktop */
        @media (min-width:992px) and (max-width:1199px) {
          .tw { border-radius:20px; }
          .mt thead th { font-size:0.68rem !important; padding:0.9rem 0.6rem !important; letter-spacing:0.7px !important; }
          .mt tbody td { font-size:0.82rem !important; padding:0.9rem 0.6rem !important; }
        }
        /* Desktop */
        @media (min-width:1200px) {
          .tw { border-radius:22px; }
          .mt thead th { font-size:0.7rem !important; padding:1rem 0.75rem !important; letter-spacing:0.8px !important; }
          .mt tbody td { font-size:0.85rem !important; padding:1rem 0.75rem !important; }
        }

        /* ═══ LANDSCAPE PHONE ═══ */
        @media (max-height:500px) and (orientation:landscape) {
          .modal-body { max-height:50vh; overflow-y:auto; -webkit-overflow-scrolling:touch; }
        }

        /* ═══ BUTTONS ═══ */
        .ba {
          display:inline-flex !important; align-items:center !important; justify-content:center !important;
          border-radius:8px !important; transition:all 0.2s !important; border:1px solid transparent !important;
          padding:0 !important;
        }
        .ba:active { transform:scale(0.93) !important; }
        @media (hover:hover) { .ba:hover { transform:scale(1.08) !important; } }
        @media (hover:none) and (pointer:coarse) { .ba { width:44px !important; height:44px !important; } }

        @media (max-width:375px) { .ba { width:32px !important; height:32px !important; border-radius:7px !important; } }
        @media (min-width:376px) and (max-width:575px) { .ba { width:34px !important; height:34px !important; } }
        @media (min-width:576px) and (max-width:991px) { .ba { width:36px !important; height:36px !important; border-radius:9px !important; } }
        @media (min-width:992px) { .ba { width:38px !important; height:38px !important; border-radius:10px !important; } }

        .ba.btn-outline-info { border-color:#0369a1 !important; color:#0369a1 !important; }
        .ba.btn-outline-info:hover,.ba.btn-outline-info:active { background:rgba(34,211,238,0.14) !important; }
        .ba.btn-outline-primary { border-color:#0B3D91 !important; color:#0B3D91 !important; }
        .ba.btn-outline-primary:hover,.ba.btn-outline-primary:active { background:rgba(99,102,241,0.14) !important; }
        .ba.btn-outline-danger { border-color:#dc2626 !important; color:#dc2626 !important; }
        .ba.btn-outline-danger:hover,.ba.btn-outline-danger:active { background:rgba(239,68,68,0.14) !important; }

        .bpm {
          background:linear-gradient(135deg,#0B3D91,#093278) !important;
          border:none !important; border-radius:12px !important; font-weight:600 !important;
          transition:all 0.3s !important;
          box-shadow:0 4px 14px rgba(99,102,241,0.3) !important;
          white-space:nowrap;
        }
        .bpm:active { transform:scale(0.96) !important; }
        @media (hover:hover) { .bpm:hover { transform:translateY(-2px) !important; box-shadow:0 8px 24px rgba(99,102,241,0.4) !important; } }

        .bom {
          border-color:#e2e8f0 !important; color:#64748b !important;
          border-radius:12px !important; transition:all 0.3s !important; white-space:nowrap;
        }
        .bom:active { background:#f0f4fb !important; }
        @media (hover:hover) { .bom:hover { background:#f0f4fb !important; border-color:#0B3D91 !important; color:#0d1b2a !important; } }

        /* ═══ FILTER ═══ */
        .fc { background:linear-gradient(145deg,#ffffff,#f0f4fb) !important; border:1px solid #e2e8f0 !important; }
        @media (max-width:375px) { .fc { border-radius:12px !important; } }
        @media (min-width:376px) and (max-width:767px) { .fc { border-radius:14px !important; } }
        @media (min-width:768px) { .fc { border-radius:16px !important; } }

        .si {
          background:#ffffff !important; border:1px solid #e2e8f0 !important;
          color:#0d1b2a !important; transition:all 0.3s !important;
        }
        .si:focus { border-color:#0B3D91 !important; box-shadow:0 0 0 3px rgba(99,102,241,0.2) !important; }
        .si::placeholder { color:#64748b !important; }

        .fsel {
          background:#ffffff !important; border:1px solid #e2e8f0 !important;
          color:#0d1b2a !important; cursor:pointer !important; transition:all 0.3s !important;
          -webkit-appearance:none;
        }
        .fsel:focus { border-color:#0B3D91 !important; box-shadow:0 0 0 3px rgba(99,102,241,0.2) !important; }
        .fsel option { background:#ffffff; color:#0d1b2a; }

        @media (max-width:575px) {
          .si,.fsel { font-size:16px !important; border-radius:10px !important; padding:0.55rem 0.7rem !important; }
        }
        @media (min-width:576px) and (max-width:767px) {
          .si,.fsel { font-size:0.85rem !important; border-radius:11px !important; padding:0.6rem 0.8rem !important; }
        }
        @media (min-width:768px) {
          .si,.fsel { font-size:0.85rem !important; border-radius:12px !important; padding:0.65rem 1rem !important; }
        }

        /* ═══ MODALS ═══ */
        .modal-content {
          background:#ffffff !important; border:1px solid #e2e8f0 !important; color:#0d1b2a !important;
        }
        @media (max-width:575px) {
          .modal-content { border-radius:20px 20px 0 0 !important; margin:0 !important; }
          .modal-dialog { margin:0 !important; max-width:100% !important; }
          .modal-body { max-height:68vh; overflow-y:auto; -webkit-overflow-scrolling:touch; }
        }
        @media (min-width:576px) { .modal-content { border-radius:20px !important; } }
        @media (min-width:992px) { .modal-content { border-radius:24px !important; } }

        .modal-header { border-bottom:1px solid #e2e8f0 !important; }
        .modal-footer { border-top:1px solid #e2e8f0 !important; }

        @media (max-width:575px) {
          .modal-header { padding:1rem !important; }
          .modal-body { padding:0.85rem !important; }
          .modal-footer { padding:0.85rem !important; }
          .modal-title { font-size:0.92rem !important; }
        }
        @media (min-width:576px) and (max-width:991px) {
          .modal-header { padding:1.25rem !important; }
          .modal-body { padding:1.25rem !important; }
          .modal-footer { padding:1rem !important; }
          .modal-title { font-size:1.05rem !important; }
        }
        @media (min-width:992px) {
          .modal-header { padding:1.5rem !important; }
          .modal-body { padding:1.5rem !important; }
          .modal-footer { padding:1.25rem !important; }
          .modal-title { font-size:1.15rem !important; }
        }

        /* ═══ FORM ═══ */
        .form-control,.form-select {
          background:#ffffff !important; border:1px solid #e2e8f0 !important;
          color:#0d1b2a !important;
        }
        .form-control:focus,.form-select:focus {
          border-color:#0B3D91 !important; box-shadow:0 0 0 3px rgba(99,102,241,0.2) !important;
          background:#ffffff !important; color:#0d1b2a !important;
        }
        .form-label { color:#64748b !important; font-weight:500 !important; }
        .form-control::placeholder { color:#64748b !important; }

        @media (max-width:575px) {
          .form-control,.form-select { font-size:16px !important; border-radius:10px !important; padding:0.6rem 0.8rem !important; }
          .form-label { font-size:0.78rem !important; margin-bottom:0.35rem !important; }
          textarea.form-control { min-height:60px; }
        }
        @media (min-width:576px) {
          .form-control,.form-select { font-size:0.85rem !important; border-radius:12px !important; padding:0.7rem 1rem !important; }
          .form-label { font-size:0.85rem !important; margin-bottom:0.5rem !important; }
          textarea.form-control { min-height:80px; }
        }

        .input-group-text {
          background:#f0f4fb !important; border-color:#e2e8f0 !important; color:#64748b !important;
        }
        @media (max-width:575px) { .input-group-text { border-radius:10px 0 0 10px !important; padding:0.55rem 0.65rem !important; } }
        @media (min-width:576px) { .input-group-text { border-radius:12px 0 0 12px !important; } }

        .form-check-input {
          background-color:#e2e8f0 !important; border-color:#cbd5e1 !important; cursor:pointer;
        }
        .form-check-input:checked { background-color:#0B3D91 !important; border-color:#0B3D91 !important; }
        @media (hover:none) and (pointer:coarse) { .form-check-input { width:3em !important; height:1.5em !important; } }
        .form-check-label { color:#0d1b2a !important; }

        /* ═══ MISC ═══ */
        .badge { font-weight:500 !important; }
        .alert { border:none !important; }
        @media (max-width:575px) { .alert { border-radius:12px !important; font-size:0.8rem !important; } }
        @media (min-width:576px) { .alert { border-radius:14px !important; font-size:0.85rem !important; } }

        .ct { color:#0369a1; background:rgba(34,211,238,0.1); font-family:'JetBrains Mono','Courier New',monospace; }
        @media (max-width:375px) { .ct { padding:0.1rem 0.3rem; border-radius:3px; font-size:0.62rem; } }
        @media (min-width:376px) and (max-width:575px) { .ct { padding:0.12rem 0.35rem; border-radius:4px; font-size:0.68rem; } }
        @media (min-width:576px) { .ct { padding:0.18rem 0.45rem; border-radius:5px; font-size:0.78rem; } }

        .es {
          background:linear-gradient(145deg,#ffffff,#f0f4fb); border:1px dashed #e2e8f0;
        }
        @media (max-width:575px) { .es { border-radius:14px; padding:1.5rem 0.8rem; } }
        @media (min-width:576px) { .es { border-radius:20px; padding:3rem 1.5rem; } }

        .dc { background:linear-gradient(145deg,#f0f4fb,#f0f4fb) !important; border:1px solid #e2e8f0 !important; }
        @media (max-width:575px) { .dc { border-radius:14px !important; } }
        @media (min-width:576px) { .dc { border-radius:16px !important; } }

        .dac {
          background:linear-gradient(145deg,#f0f4fb,#f0f4fb) !important;
          border:1px solid #e2e8f0 !important; transition:all 0.3s !important;
        }
        @media (max-width:575px) { .dac { border-radius:12px !important; } }
        @media (min-width:576px) { .dac { border-radius:16px !important; } }
        .dac:active { transform:scale(0.97) !important; }
        @media (hover:hover) { .dac:hover { transform:translateY(-4px) !important; } }

        .tooltip-inner { background:#f0f4fb !important; border:1px solid #e2e8f0 !important; border-radius:8px !important; }
        .tooltip .tooltip-arrow::before { border-top-color:#f0f4fb !important; }

        ::-webkit-scrollbar { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background:#ffffff; }
        ::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:3px; }
        ::-webkit-scrollbar-thumb:hover { background:#e2e8f0; }
        * { scrollbar-width:thin; scrollbar-color:#e2e8f0 #ffffff; }

        /* ═══ FAB ═══ */
        .fab-wrap { position:fixed; bottom:96px; right:12px; z-index:100000; }
        .fab-btn {
          width:54px; height:54px; border-radius:50%;
          background:linear-gradient(135deg,#0B3D91,#093278);
          border:none; color:#0d1b2a; font-size:1.4rem;
          display:flex; align-items:center; justify-content:center;
          box-shadow:0 6px 20px rgba(99,102,241,0.4);
          cursor:pointer; transition:all 0.3s;
        }
        .fab-btn:active { transform:scale(0.88); }
        .fab-m {
          position:absolute; bottom:64px; right:0;
          background:#0B3D91; border:1px solid #e2e8f0;
          border-radius:14px; padding:0.4rem;
          min-width:170px; animation:slideUp 0.2s ease;
          box-shadow:0 16px 32px rgba(0,0,0,0.5);
        }
        .fab-mi {
          display:flex; align-items:center; gap:0.65rem;
          padding:0.65rem 0.75rem; border-radius:9px;
          color:#64748b; background:none; border:none;
          width:100%; font-size:0.82rem; font-weight:500;
          cursor:pointer; transition:all 0.2s;
        }
        .fab-mi:active { background:rgba(99,102,241,0.14); color:#0d1b2a; }
        @media (hover:hover) { .fab-mi:hover { background:rgba(99,102,241,0.1); color:#0d1b2a; } }

        @media print {
          body { background:#0d1b2a !important; color:#ffffff !important; }
          .fab-wrap,.bpm,.bom,.fc { display:none !important; }
        }
      `}</style>

      <div className="safe-wrap">
        {/* ═══ HEADER ═══ */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: sp.gap }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="fw-bold mb-0" style={{ color: t.primary, fontSize: device.isGalaxyFold ? "0.82rem" : device.isMobile ? fs.lg : device.isTablet ? "1.3rem" : "1.5rem" }}>
              <i className="bi bi-cash-stack me-2" />
              {device.isMobile ? "Ubukungu" : "Ubukungu bw'Abaturage"}
            </h2>
            {!device.isMobile && (
              <small style={{ color: t.muted, fontSize: fs.sm }}>
                Gucunga no gukurikirana ubukungu • <strong style={{ color: t.primary }}>{filteredData.length}</strong> records
              </small>
            )}
          </div>
          {!device.isMobile && (
            <div className="d-flex gap-2 flex-wrap">
              <Button className="bpm" onClick={handleAdd} style={{ padding: device.isTablet ? "0.6rem 1rem" : "0.8rem 1.4rem", fontSize: fs.sm }}>
                <i className="bi bi-plus-circle me-2" />Ongeramo
              </Button>
              <Button className="bom" variant="outline-light" onClick={loadData} style={{ padding: device.isTablet ? "0.55rem 0.75rem" : "0.7rem 1rem" }}>
                <i className="bi bi-arrow-clockwise" />
              </Button>
              <Button className="bom" variant="outline-success" onClick={exportToCSV} style={{ padding: device.isTablet ? "0.55rem 0.75rem" : "0.7rem 1rem" }}>
                <i className="bi bi-download me-1" />Excel
              </Button>
            </div>
          )}
        </div>

        {/* ALERT */}
        {alert.show && (
          <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: "", variant: "" })} className="mb-3 shadow">
            {alert.message}
          </Alert>
        )}

        {/* ═══ STATS ═══ */}
        <Row className="g-2 g-md-3" style={{ marginBottom: sp.gap }}>
          {[
            { title: device.isMobile ? "Bose" : "Abaturage Bose", val: stats.total, color: t.primary, icon: "people-fill", pct: 100 },
            { title: device.isMobile ? "Ubutaka" : "Bafite Ubutaka", val: stats.withLand, color: t.success, icon: "geo-alt-fill", pct: stats.total ? Math.round((stats.withLand / stats.total) * 100) : 0 },
            { title: device.isMobile ? "Amatungo" : "Bafite Amatungo", val: stats.withAnimals, color: t.warning, icon: "piggy-bank-fill", pct: stats.total ? Math.round((stats.withAnimals / stats.total) * 100) : 0 },
            { title: device.isMobile ? "Konti" : "Bafite Konti", val: stats.withAccount, color: t.info, icon: "bank", pct: stats.total ? Math.round((stats.withAccount / stats.total) * 100) : 0 },
          ].map((s, i) => {
            const ringSize = device.isGalaxyFold ? 30 : device.isMiniPhone ? 36 : device.isSmallPhone ? 42 : device.isMediumPhone ? 46 : device.isLargePhone ? 50 : device.isSmallTablet ? 52 : device.isTablet ? 56 : device.isLargeTablet ? 62 : 66;
            const innerSize = ringSize - (device.isGalaxyFold ? 10 : 14);
            return (
              <Col key={i} xs={6} md={3}>
                <Card className="sc border-0 h-100" style={{ borderRadius: sp.radius }}>
                  <Card.Body className="text-center" style={{ padding: sp.card }}>
                    <div className="ir" style={{ "--rc": s.color, width: ringSize, height: ringSize, marginBottom: device.isMobile ? "0.3rem" : "0.6rem" }}>
                      <div className="iri" style={{ width: innerSize, height: innerSize }}>
                        <i className={`bi bi-${s.icon}`} style={{ fontSize: fs.icon, color: s.color }} />
                      </div>
                    </div>
                    <div style={{ fontSize: fs.stat, fontWeight: 800, color: t.text, lineHeight: 1.2 }}>{s.val}</div>
                    <div style={{ fontSize: device.isGalaxyFold ? "0.45rem" : fs.xs, fontWeight: 600, color: t.muted, textTransform: "uppercase", letterSpacing: "0.3px", marginTop: "0.2rem" }}>
                      {s.title}
                    </div>
                    <Badge style={{ background: `${s.color}20`, color: s.color, fontSize: device.isGalaxyFold ? "0.45rem" : fs.xs, padding: "0.15rem 0.35rem", marginTop: "0.2rem" }}>
                      {s.pct}%
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* ═══ FILTERS ═══ */}
        <Card className="fc border-0 shadow" style={{ marginBottom: sp.gap }}>
          <Card.Body style={{ padding: sp.card }}>
            <Row className="g-2">
              <Col xs={12} md={7} lg={8}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi bi-search" style={{ fontSize: fs.sm }} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={device.isMobile ? "Shakisha..." : "Shakisha amazina, ID, umurimo, aho akorera..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="si"
                    style={{ borderRadius: "0 10px 10px 0" }}
                  />
                  {searchTerm && (
                    <Button variant="link" onClick={() => setSearchTerm("")}
                      style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", zIndex: 5, background: "none", border: "none", color: t.dim, padding: "0.4rem" }}>
                      <i className="bi bi-x-lg" />
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col xs={12} md={5} lg={4}>
                <Form.Select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="fsel">
                  <option value="all">📋 Byose ({data.length})</option>
                  <option value="ubutaka">🌍 Ubutaka ({stats.withLand})</option>
                  <option value="amatungo">🐄 Amatungo ({stats.withAnimals})</option>
                  <option value="konti">🏦 Konti ({stats.withAccount})</option>
                </Form.Select>
              </Col>
            </Row>
            {(searchTerm || filterType !== "all") && (
              <div className="mt-2">
                <small style={{ color: t.muted, fontSize: fs.xs }}>
                  <i className="bi bi-funnel me-1" />
                  Byasanze: <strong style={{ color: t.primary }}>{filteredData.length}</strong> / {data.length}
                  {searchTerm && <span className="ms-2">• "{searchTerm}"</span>}
                </small>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ═══ DATA COUNT BAR ═══ */}
        {!loading && filteredData.length > 0 && (
          <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: sp.gap, padding: "0 0.15rem" }}>
            <small style={{ color: t.muted, fontSize: fs.xs }}>
              <i className="bi bi-database me-1" />
              {device.isMobile
                ? `${visibleData.length} / ${filteredData.length}`
                : `Erekana ${visibleData.length} muri ${filteredData.length} byose`
              }
            </small>
            {filteredData.length > 50 && visibleCount < filteredData.length && (
              <Button
                size="sm"
                variant="link"
                onClick={() => setVisibleCount(filteredData.length)}
                style={{ color: t.primary, fontSize: fs.xs, padding: 0, textDecoration: "none" }}
              >
                Erekana byose ▼
              </Button>
            )}
          </div>
        )}

        {/* ═══ MAIN CONTENT ═══ */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: t.primary, width: device.isMobile ? "2rem" : "2.5rem", height: device.isMobile ? "2rem" : "2.5rem" }} />
            <p className="mt-3" style={{ color: t.muted, fontSize: fs.md }}>Gutegura amakuru...</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="es text-center">
            <i className="bi bi-search" style={{ fontSize: device.isMobile ? "2.5rem" : "3.5rem", color: "#e2e8f0" }} />
            <h5 className="mt-3" style={{ color: t.muted, fontSize: fs.lg }}>Nta makuru yabonetse</h5>
            <p style={{ color: t.faint, fontSize: fs.sm }}>
              {searchTerm || filterType !== "all" ? "Hindura ibyo ushakisha" : "Tangira ukongeramo amakuru"}
            </p>
            {!searchTerm && filterType === "all" && (
              <Button className="bpm mt-2" onClick={handleAdd} style={{ padding: "0.6rem 1rem", fontSize: fs.sm }}>
                <i className="bi bi-plus-circle me-2" />Ongeramo
              </Button>
            )}
          </div>
        ) : device.isMobile ? (
          /* ═══ MOBILE: CARD LAYOUT ═══ */
          <div ref={scrollRef}>
            {visibleData.map((item, i) => (
              <MobileCard
                key={item.ubukungu_id}
                item={item}
                index={i}
                isLast={i === visibleData.length - 1}
              />
            ))}
            {visibleCount < filteredData.length && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" style={{ color: t.primary }} />
                <small className="ms-2" style={{ color: t.muted, fontSize: fs.xs }}>Gutegura ibindi...</small>
              </div>
            )}
          </div>
        ) : (
          /* ═══ DESKTOP/TABLET: TABLE LAYOUT ═══ */
          <div className="tw" ref={scrollRef}>
            <div className="table-responsive" style={{ WebkitOverflowScrolling: "touch", maxHeight: device.isLandscapePhone ? "55vh" : "none" }}>
              <Table hover className="mt">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: 45 }}>#</th>
                    <th>Amazina</th>
                    <th>ID</th>
                    {device.isDesktopOrAbove && <th>Umurimo</th>}
                    {device.isDesktopOrAbove && <th>Aho Akorera</th>}
                    {device.isDesktop && <th>Inkomoko</th>}
                    <th className="text-center">Ubutaka</th>
                    <th className="text-center">Amatungo</th>
                    <th className="text-center">Konti</th>
                    <th className="text-center" style={{ width: device.isTablet ? 120 : 140 }}>Ibikorwa</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleData.map((item, i) => (
                    <tr key={item.ubukungu_id} ref={i === visibleData.length - 1 ? lastItemRef : null}>
                      <td className="text-center" style={{ color: t.dim }}>{i + 1}</td>
                      <td>
                        <div className="fw-semibold" style={{
                          color: t.text,
                          maxWidth: device.isTablet ? 140 : device.isLargeTablet ? 200 : "auto",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {item.amazina_y_umuturage}
                        </div>
                        {!device.isDesktopOrAbove && (
                          <small style={{ color: t.faint, fontSize: "0.65rem" }}>
                            {item.umurimo || "—"} • {item.aho_akorera || "—"}
                          </small>
                        )}
                      </td>
                      <td><span className="ct">{item.muturage_id}</span></td>
                      {device.isDesktopOrAbove && <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.umurimo || "—"}</td>}
                      {device.isDesktopOrAbove && <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.aho_akorera || "—"}</td>}
                      {device.isDesktop && <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.inkomoko_y_amafaranga || "—"}</td>}
                      <td className="text-center"><BoolBadge val={item.afite_ubutaka} /></td>
                      <td className="text-center"><BoolBadge val={item.afite_amatungo} /></td>
                      <td className="text-center"><BoolBadge val={item.afite_konti_yo_kuzigama} yesColor="info" /></td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Reba</Tooltip>}>
                            <Button className="ba btn-outline-info" onClick={() => handleViewDetails(item)}><i className="bi bi-eye" /></Button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Hindura</Tooltip>}>
                            <Button className="ba btn-outline-primary" onClick={() => handleEdit(item)}><i className="bi bi-pencil" /></Button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Siba</Tooltip>}>
                            <Button className="ba btn-outline-danger" onClick={() => handleDelete(item.ubukungu_id)}><i className="bi bi-trash" /></Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {visibleCount < filteredData.length && (
              <div className="text-center py-3" style={{ borderTop: `1px solid ${t.border3}` }}>
                <Spinner animation="border" size="sm" style={{ color: t.primary }} />
                <small className="ms-2" style={{ color: t.muted }}>Gutegura ibindi ({visibleCount}/{filteredData.length})...</small>
              </div>
            )}
          </div>
        )}

        {/* ═══ MOBILE FAB ═══ */}
        {device.isMobile && (
          <div className="fab-wrap">
            {showMobileMenu && (
              <div className="fab-m">
                <button className="fab-mi" onClick={handleAdd}>
                  <i className="bi bi-plus-circle" style={{ color: t.success, fontSize: "1.05rem" }} />Ongeramo
                </button>
                <button className="fab-mi" onClick={() => { loadData(); setShowMobileMenu(false); }}>
                  <i className="bi bi-arrow-clockwise" style={{ color: t.info, fontSize: "1.05rem" }} />Fungura
                </button>
                <button className="fab-mi" onClick={exportToCSV}>
                  <i className="bi bi-download" style={{ color: t.warning, fontSize: "1.05rem" }} />Excel
                </button>
                {filteredData.length > visibleCount && (
                  <button className="fab-mi" onClick={() => { setVisibleCount(filteredData.length); setShowMobileMenu(false); }}>
                    <i className="bi bi-list-ul" style={{ color: t.primaryLt, fontSize: "1.05rem" }} />Byose ({filteredData.length})
                  </button>
                )}
              </div>
            )}
            <button className="fab-btn" onClick={() => setShowMobileMenu(!showMobileMenu)}
              style={{ transform: showMobileMenu ? "rotate(45deg)" : "none", transition: "transform 0.3s" }}>
              <i className="bi bi-plus-lg" />
            </button>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════
          ADD/EDIT MODAL
      ═══════════════════════════════════════════ */}
      <Modal show={show} onHide={() => setShow(false)} size={device.isMobile ? undefined : "lg"} centered={!device.isMobile} fullscreen={device.isMobile ? true : undefined} scrollable>
        <Modal.Header closeButton closeVariant="white" style={{ background: `linear-gradient(135deg,${t.primary},${t.primaryDk})`, border: "none" }}>
          <Modal.Title className="text-white">
            <i className={`bi ${editingId ? "bi-pencil-square" : "bi-plus-circle"} me-2`} />
            {editingId ? "Hindura" : "Ongeramo Amakuru"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: t.bg }}>
          <Form>
            <Row className="g-3">
              <Col xs={12} md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Umuturage <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-person" /></InputGroup.Text>
                    <Form.Select name="muturage_id" value={form.muturage_id} onChange={handleCitizenSelect}
                      isInvalid={!!formErrors.muturage_id} style={{ borderRadius: "0 10px 10px 0" }}>
                      <option value="">-- Hitamo umuturage --</option>
                      {citizens.map((c) => (
                        <option key={c.umuturage_id} value={c.umuturage_id}>
                          {c.full_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{formErrors.muturage_id}</Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Umurimo</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-briefcase" /></InputGroup.Text>
                    <Form.Control name="umurimo" value={form.umurimo} onChange={handleChange} placeholder="Akoramo iki?" style={{ borderRadius: "0 10px 10px 0" }} />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Aho Akorera</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-building" /></InputGroup.Text>
                    <Form.Control name="aho_akorera" value={form.aho_akorera} onChange={handleChange} placeholder="Akorerera hehe?" style={{ borderRadius: "0 10px 10px 0" }} />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Inkomoko y'Amafaranga</Form.Label>
                  <Form.Control as="textarea" rows={device.isMobile ? 2 : 3} name="inkomoko_y_amafaranga" value={form.inkomoko_y_amafaranga} onChange={handleChange} placeholder="Ubuhinzi, ubucuruzi, umushahara..." />
                </Form.Group>
              </Col>
              <Col xs={12}><hr style={{ borderColor: t.border2 }} /><p style={{ color: t.muted, fontSize: fs.sm }} className="mb-3"><i className="bi bi-info-circle me-1" />Hitamo ibyo afite:</p></Col>
              {[
                { id: "ubutaka", name: "afite_ubutaka", label: "Ubutaka", icon: "geo-alt", clr: t.success },
                { id: "amatungo", name: "afite_amatungo", label: "Amatungo", icon: "piggy-bank", clr: t.warning },
                { id: "konti", name: "afite_konti_yo_kuzigama", label: "Konti", icon: "bank", clr: t.info },
              ].map((sw) => (
                <Col xs={12} sm={4} key={sw.id}>
                  <div style={{
                    background: t.card2, borderRadius: "12px",
                    padding: device.isMobile ? "0.65rem" : "0.85rem",
                    border: `1px solid ${form[sw.name] ? sw.clr : t.border2}`,
                    transition: "all 0.3s",
                  }}>
                    <Form.Check type="switch" id={sw.id} name={sw.name} checked={form[sw.name] === 1} onChange={handleChange}
                      label={<span className="fw-semibold" style={{ fontSize: fs.sm }}><i className={`bi bi-${sw.icon} me-1`} style={{ color: sw.clr }} />{sw.label}</span>} />
                  </div>
                </Col>
              ))}
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ flexDirection: device.isMobile ? "column" : "row", gap: device.isMobile ? "0.5rem" : undefined }}>
          <Button variant="secondary" onClick={() => setShow(false)} style={{ borderRadius: "12px", width: device.isMobile ? "100%" : "auto", order: device.isMobile ? 2 : 1 }}>
            <i className="bi bi-x-circle me-1" />Funga
          </Button>
          <Button onClick={handleSubmit} style={{
            background: editingId ? `linear-gradient(135deg,${t.warning},#d97706)` : `linear-gradient(135deg,${t.success},#16a34a)`,
            border: "none", borderRadius: "12px", fontWeight: 600,
            width: device.isMobile ? "100%" : "auto", order: device.isMobile ? 1 : 2,
            padding: device.isMobile ? "0.7rem" : undefined,
          }}>
            <i className={`bi me-1 ${editingId ? "bi-save" : "bi-check-circle"}`} />
            {editingId ? "Hindura" : "Bika"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════
          DETAILS MODAL
      ═══════════════════════════════════════════ */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)} centered={!device.isMobile} size={device.isMobile ? undefined : "lg"} fullscreen={device.isMobile ? true : undefined} scrollable>
        <Modal.Header closeButton closeVariant="white" style={{ background: `linear-gradient(135deg,${t.info},#0369a1)`, border: "none" }}>
          <Modal.Title className="text-white"><i className="bi bi-eye me-2" />Amakuru Arambuye</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: t.bg }}>
          {selectedItem && (
            <Row className="g-3">
              <Col xs={12}>
                <Card className="dc border-0">
                  <Card.Body className="text-center" style={{ padding: sp.card }}>
                    <h4 className="fw-bold mb-2" style={{ color: t.primary, fontSize: device.isMobile ? fs.lg : "1.3rem" }}>{selectedItem.amazina_y_umuturage}</h4>
                    <span className="ct" style={{ fontSize: device.isMobile ? fs.md : "0.95rem", padding: "0.35rem 0.7rem" }}>{selectedItem.muturage_id}</span>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}><DetailField label="Umurimo" value={selectedItem.umurimo} color={t.success} /></Col>
              <Col xs={12} md={6}><DetailField label="Aho Akorera" value={selectedItem.aho_akorera} color={t.warning} /></Col>
              <Col xs={12}><DetailField label="Inkomoko y'Amafaranga" value={selectedItem.inkomoko_y_amafaranga} color={t.info} /></Col>
              <Col xs={12}>
                <hr style={{ borderColor: t.border2 }} />
                <h6 style={{ color: t.muted, fontSize: fs.sm }} className="mb-3"><i className="bi bi-list-check me-2" />Ibintu Afite</h6>
                <Row className="g-2">
                  {[
                    { label: "Ubutaka", val: selectedItem.afite_ubutaka, icon: "geo-alt-fill", clr: t.success },
                    { label: "Amatungo", val: selectedItem.afite_amatungo, icon: "piggy-bank-fill", clr: t.warning },
                    { label: "Konti", val: selectedItem.afite_konti_yo_kuzigama, icon: "bank", clr: t.info },
                  ].map((a, i) => (
                    <Col xs={4} key={i}>
                      <Card className="dac border-0">
                        <Card.Body className="text-center" style={{ padding: device.isMobile ? "0.6rem 0.35rem" : "1.1rem" }}>
                          <i className={`bi bi-${a.icon}`} style={{ fontSize: device.isMobile ? "1.3rem" : "2.2rem", color: a.val ? a.clr : t.danger }} />
                          <h6 className="mt-1 mb-1" style={{ color: t.muted, fontSize: device.isMobile ? "0.6rem" : fs.sm }}>{a.label}</h6>
                          <Badge bg={a.val ? "success" : "danger"} style={{ fontSize: device.isMobile ? "0.55rem" : fs.xs, padding: "0.2rem 0.4rem" }}>
                            {a.val ? "Yego" : "Oya"}
                          </Badge>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer style={{ flexDirection: device.isMobile ? "column" : "row", gap: device.isMobile ? "0.5rem" : undefined }}>
          <Button onClick={() => { setShowDetails(false); handleEdit(selectedItem); }}
            style={{ background: `linear-gradient(135deg,${t.primary},${t.primaryDk})`, border: "none", borderRadius: "12px", fontWeight: 600, width: device.isMobile ? "100%" : "auto" }}>
            <i className="bi bi-pencil-square me-1" />Hindura
          </Button>
          <Button variant="secondary" onClick={() => setShowDetails(false)} style={{ borderRadius: "12px", width: device.isMobile ? "100%" : "auto" }}>
            <i className="bi bi-x-circle me-1" />Funga
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════
          DELETE MODAL
      ═══════════════════════════════════════════ */}
      <Modal show={showDeleteConfirm} onHide={() => { setShowDeleteConfirm(false); setItemToDelete(null); }} centered backdrop="static">
        <Modal.Header closeButton closeVariant="white" style={{ background: `linear-gradient(135deg,${t.danger},#dc2626)`, border: "none" }}>
          <Modal.Title className="text-white"><i className="bi bi-exclamation-triangle-fill me-2" />Uremeza gusiba?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: t.bg }}>
          {itemToDelete && (
            <>
              <p style={{ color: t.muted, fontSize: fs.sm }} className="mb-2">Ushaka gusiba amakuru ya:</p>
              <h5 className="fw-bold mb-3" style={{ color: t.danger, fontSize: device.isMobile ? fs.lg : "1.2rem" }}>{itemToDelete.amazina_y_umuturage}</h5>
              <div style={{ color: t.muted, fontSize: fs.sm }}>ID: <span className="ct">{itemToDelete.muturage_id}</span></div>
              <Alert variant="danger" className="mt-3 mb-0" style={{ background: `${t.danger}14`, border: `1px solid ${t.danger}30`, color: "#dc2626", fontSize: fs.sm }}>
                <i className="bi bi-exclamation-triangle me-2" />Ntishobora guhindurwa!
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ flexDirection: device.isMobile ? "column" : "row", gap: device.isMobile ? "0.5rem" : undefined }}>
          <Button variant="outline-light" onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}
            style={{ borderRadius: "12px", borderColor: t.border2, width: device.isMobile ? "100%" : "auto", order: device.isMobile ? 2 : 1 }}>
            <i className="bi bi-x-circle me-1" />Oya
          </Button>
          <Button variant="danger" onClick={confirmDelete}
            style={{ borderRadius: "12px", fontWeight: 600, width: device.isMobile ? "100%" : "auto", order: device.isMobile ? 1 : 2, padding: device.isMobile ? "0.7rem" : undefined }}>
            <i className="bi bi-trash me-1" />Yego – Siba
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Ubukungu;